import { App, type URLOpenListenerEvent } from "@capacitor/app"
import { Capacitor, registerPlugin } from "@capacitor/core"
import { goto } from "$app/navigation"
import { selectTab } from "@store/pageTracker"

declare global {
    interface Window {
        __ACIM_SCREENSHOT_PATH__?: string
    }
}

interface ScreenshotRoutePlugin {
    getPath(): Promise<{ path?: string }>
}

const ScreenshotRoute = registerPlugin<ScreenshotRoutePlugin>("ScreenshotRoute")

function normalizeInternalPath(path: string | null) {
    if (!path) return null
    const normalizedPath = path.startsWith("/") ? path : `/${path}`

    if (normalizedPath.startsWith("//")) return null
    return normalizedPath
}

function pathFromAppUrl(url: string) {
    try {
        const parsedURL = new URL(url)

        if (parsedURL.protocol === "acimce:" && parsedURL.hostname === "open") {
            return normalizeInternalPath(
                `${parsedURL.pathname}${parsedURL.search}${parsedURL.hash}`,
            )
        }

        if (parsedURL.hostname === "acimce.app" || parsedURL.hostname.endsWith(".acimce.app")) {
            return normalizeInternalPath(
                `${parsedURL.pathname}${parsedURL.search}${parsedURL.hash}`,
            )
        }
    } catch (e) {
        // Fall back to the old string split below.
    }

    if (url.includes(".app")) return normalizeInternalPath(url.split(".app").pop() ?? "")
    return null
}

function pathnameFromInternalPath(path: string) {
    return path.split(/[?#]/)[0] ?? path
}

let lastHandledInternalPath: string | null = null
let lastHandledURL: string | null = null

async function handleInternalPath(path: string | null) {
    const normalizedPath = normalizeInternalPath(path)
    if (!normalizedPath || lastHandledInternalPath === normalizedPath) return

    const pathname = pathnameFromInternalPath(normalizedPath)

    // If we're navigating from the outside, we want to make sure we
    // don't override a local users tabs
    if (pathname.startsWith("/book/") || pathname.startsWith("/:")) {
        selectTab("abandoned")
    }

    if (pathname.startsWith("/:")) {
        console.log("Detected redirection: ", normalizedPath)
        lastHandledInternalPath = normalizedPath
        await goto("/book/" + normalizedPath.substring(2)).catch((error) => {
            lastHandledInternalPath = null
            console.warn(error)
        })
        return
    }

    lastHandledInternalPath = normalizedPath
    await goto(normalizedPath).catch((error) => {
        lastHandledInternalPath = null
        console.warn(error)
    })
}

function handleScreenshotPath(path: string) {
    setTimeout(() => {
        void handleInternalPath(path)
    }, 0)
}

async function handleNativeScreenshotPath() {
    if (Capacitor.getPlatform() !== "ios") return

    try {
        const { path } = await ScreenshotRoute.getPath()
        if (path) handleScreenshotPath(path)
    } catch (error) {
        console.warn("Unable to read native screenshot route", error)
    }
}

function handleAppUrl(url: string) {
    if (lastHandledURL === url) return
    lastHandledURL = url

    console.log("Deep link fired: ", url)
    // Example url: https://acimce.app/book/T-3 or acimce://open/book/T-3
    const path = pathFromAppUrl(url)
    if (!path) return

    void handleInternalPath(path)
}

/**
 * Setup deep links
 * https://capacitorjs.com/docs/guides/deep-links
 */
export function setupDeepLinks() {
    void handleNativeScreenshotPath()

    App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
        handleAppUrl(event.url)
    })

    App.getLaunchUrl()
        .then((event) => {
            if (event?.url) handleAppUrl(event.url)
        })
        .catch(console.warn)

    if (window.__ACIM_SCREENSHOT_PATH__) {
        handleScreenshotPath(window.__ACIM_SCREENSHOT_PATH__)
    }

    window.addEventListener("acim:screenshot-path", (event) => {
        const screenshotPath = (event as CustomEvent<unknown>).detail
        if (typeof screenshotPath === "string") handleScreenshotPath(screenshotPath)
    })
}
